/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import styles from './index.module.scss';

type DialogContextValue = {
  open: boolean;
  titleId: string;
  descriptionId: string;
  closeOnEscape: boolean;
  closeOnOverlayClick: boolean;
  lockScroll: boolean;
  setOpen: (open: boolean) => void;
};

export type DialogProps = {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  lockScroll?: boolean;
};

export type DialogTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
};

export type DialogContentProps = React.HTMLAttributes<HTMLDivElement> & {
  containerClassName?: string;
  overlayClassName?: string;
  /** 单独控制 Portal 遮罩层级，避免购买弹窗被全屏业务工作区遮挡。 */
  overlayZIndex?: number;
  autoHeight?: boolean;
  fullscreen?: boolean;
  showClose?: boolean;
  closeLabel?: string;
  closeIcon?: React.ReactNode;
  detailText?: React.ReactNode;
  detailIcon?: React.ReactNode;
  onDetailClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement>;
export type DialogTitleProps = React.HTMLAttributes<HTMLHeadingElement>;
export type DialogDescriptionProps = React.HTMLAttributes<HTMLParagraphElement>;
export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

const DialogContext = createContext<DialogContextValue | null>(null);

// useDialogContext keeps compound parts inside a Dialog root and gives a clear error in development.
function useDialogContext(componentName: string) {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error(`${componentName} must be used within Dialog`);
  }

  return context;
}

// Dialog owns controlled/uncontrolled open state for the shadcn-style compound API.
function Dialog({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  lockScroll = true,
}: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isControlled = typeof open === 'boolean';
  const actualOpen = isControlled ? !!open : innerOpen;

  const setOpen = useCallback((nextOpen: boolean) => {
    if (!isControlled) {
      setInnerOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  }, [isControlled, onOpenChange]);

  const contextValue = useMemo<DialogContextValue>(() => ({
    open: actualOpen,
    titleId,
    descriptionId,
    closeOnEscape,
    closeOnOverlayClick,
    lockScroll,
    setOpen,
  }), [
    actualOpen,
    titleId,
    descriptionId,
    closeOnEscape,
    closeOnOverlayClick,
    lockScroll,
    setOpen,
  ]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
    </DialogContext.Provider>
  );
}

// DialogTrigger opens the dialog and can either render a default button or clone a child trigger.
const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>((
  {
    asChild,
    children,
    onClick,
    ...props
  },
  ref,
) => {
  const { setOpen } = useDialogContext('DialogTrigger');

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);

    if (!event.defaultPrevented) {
      setOpen(true);
    }
  }, [onClick, setOpen]);

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{
      onClick?: React.MouseEventHandler<HTMLButtonElement>;
      ref?: React.Ref<HTMLButtonElement>;
    }>;

    return React.cloneElement(child, {
      ref,
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        child.props.onClick?.(event);
        handleClick(event);
      },
    });
  }

  return (
    <button ref={ref} type="button" onClick={handleClick} {...props}>
      {children}
    </button>
  );
});

/** 渲染弹窗遮罩、内容容器、详情入口和关闭按钮。 */
const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>((
  {
    className,
    containerClassName,
    overlayClassName,
    overlayZIndex,
    autoHeight,
    fullscreen = false,
    children,
    showClose = true,
    closeLabel = '关闭',
    closeIcon,
    detailText,
    detailIcon,
    onDetailClick,
    ...props
  },
  ref,
) => {
  const {
    open,
    titleId,
    descriptionId,
    closeOnEscape,
    closeOnOverlayClick,
    lockScroll,
    setOpen,
  } = useDialogContext('DialogContent');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open || !closeOnEscape) {
      return undefined;
    }

    // Escape follows the expected modal behavior without depending on antd or Radix.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeOnEscape, open, setOpen]);

  useEffect(() => {
    if (!open || !lockScroll) {
      return undefined;
    }

    // Keep the page behind the purchase dialog fixed while the overlay is visible.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [lockScroll, open]);

  if (!mounted || !open) {
    return null;
  }

  const content = (
    <div
      role="presentation"
      className={classNames(styles.overlay, {
        [styles.overlayFullscreen]: fullscreen,
      }, overlayClassName)}
      style={overlayZIndex === undefined ? undefined : { zIndex: overlayZIndex }}
      onMouseDown={(event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
          setOpen(false);
        }
      }}
    >
      <div
        className={classNames(styles.contentContainer, {
          [styles.contentContainerFullscreen]: fullscreen,
        }, containerClassName)}
      >
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className={classNames(styles.content, {
            [styles.contentAutoHeight]: autoHeight,
            [styles.contentFullscreen]: fullscreen,
          }, className)}
          {...props}
        >
          <div className={styles.topActions}>
            <div className={styles.topActionSpacer} />
            {detailText ? (
              <button type="button" className={styles.detailButton} onClick={onDetailClick}>
                {detailIcon || <InfoIcon />}
                <span>{detailText}</span>
              </button>
            ) : <div className={styles.detailButtonPlaceholder} />}
            {showClose ? (
              <button
                type="button"
                className={styles.closeButton}
                aria-label={closeLabel}
                onClick={() => {
                  setOpen(false);
                }}
              >
                {closeIcon || <CloseIcon />}
              </button>
            ) : <div className={styles.closeButtonPlaceholder} />}
          </div>
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
});

// DialogHeader matches the Figma title block with a 4px title/description gap.
const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>((
  { className, ...props },
  ref,
) => {
  return <div ref={ref} className={classNames(styles.header, className)} {...props} />;
});

// DialogTitle renders the primary centered title text.
const DialogTitle = React.forwardRef<HTMLHeadingElement, DialogTitleProps>((
  { className, ...props },
  ref,
) => {
  const { titleId } = useDialogContext('DialogTitle');

  return <h2 ref={ref} id={titleId} className={classNames(styles.title, className)} {...props} />;
});

// DialogDescription renders the lower-emphasis subtitle text.
const DialogDescription = React.forwardRef<HTMLParagraphElement, DialogDescriptionProps>((
  { className, ...props },
  ref,
) => {
  const { descriptionId } = useDialogContext('DialogDescription');

  return <p ref={ref} id={descriptionId} className={classNames(styles.description, className)} {...props} />;
});

// DialogFooter centers the primary purchase action at the bottom of the modal.
const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>((
  { className, ...props },
  ref,
) => {
  return <div ref={ref} className={classNames(styles.footer, className)} {...props} />;
});

// CloseIcon keeps the close affordance available without coupling this primitive to IconFont.
function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4.16699 4.16675L15.8337 15.8334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.833 4.16675L4.16634 15.8334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// InfoIcon mirrors the Figma detail icon while staying themeable through currentColor.
function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 14.1667C11.4058 14.1667 14.1667 11.4058 14.1667 8C14.1667 4.59424 11.4058 1.83333 8 1.83333C4.59424 1.83333 1.83333 4.59424 1.83333 8C1.83333 11.4058 4.59424 14.1667 8 14.1667Z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8 7.33333V11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M8 5.16667H8.00667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

DialogTrigger.displayName = 'DialogTrigger';
DialogContent.displayName = 'DialogContent';
DialogHeader.displayName = 'DialogHeader';
DialogTitle.displayName = 'DialogTitle';
DialogDescription.displayName = 'DialogDescription';
DialogFooter.displayName = 'DialogFooter';

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
};

export default Dialog;
